import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { DomainAccountState, NewDomainAccounts, TestDomainAccount } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { translator } from '../../translator';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import router from '../../../router/index';
import ScreenUrl from '../../../router/url';
import { EditAccount } from '../account/types';

export const actions: ActionTree<DomainAccountState, RootState> = {
    /**
     * ドメインアカウント一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} ignore_node_id 除外ノードID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} domain_id ドメインID
     * @returns {Promise<void>}
     */
    async search(
        { dispatch, commit },
        { word = '', ignore_node_id = undefined, pageNumber, perPage, domain_id, sort } = {}
    ) {
        const payload = {
            word: word,
            ignore_node_id: ignore_node_id,
            page_number: pageNumber,
            per_page: perPage,
            domain_id: domain_id,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.DomainAccount.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ドメインアカウントのドメインサーバー内の検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 検索対象のドメインのID
     * @param {any} group 検索セキュリティグループ名
     * @param {any} user 検索アカウント名
     * @returns {Promise<void>}
     */
    async directSearch({ dispatch, commit }, { id, group, user } = {}) {
        const payload = { id, group, user };
        try {
            commit(types.DIRECT_SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.NodeDomain.ACCOUNT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DIRECT_SEARCH_SUCCESS, { domain_accounts: resData });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.DIRECT_SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ドメインアカウント新規登録
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {NewDomainAccounts} newDomainAccounts 新規登録ドメインアカウント
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, newDomainAccounts: NewDomainAccounts) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.DomainAccount.CREATE,
                    params: newDomainAccounts,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.DomainAccount.CREATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * ドメインアカウントの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id ドメインアカウントID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.DomainAccount.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * ドメインアカウント更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} domainAccount 更新するドメインアカウント情報
     * @param {any} accountId ドメインアカウントID
     * @param {any} domainId ドメインID
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, { domainAccount, accountId, domainId } = {}) {
        const requestParams: EditAccount = domainAccount;
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.DomainAccount.UPDATE.replace(':id', accountId.toString()),
                    params: requestParams,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功時トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.DomainAccount.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * ドメインアカウント削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {Array<number>} id ドメインアカウントID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, id: Array<number>) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.DomainAccount.DESTROY,
                    params: { id },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.DomainAccount.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * ドメインアカウントの接続テスト
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {TestDomainAccount} testDomainAccount テストするドメインアカウント情報
     * @returns {Promise<void>}
     */
    async connection({ dispatch, commit, rootState }, testDomainAccount: TestDomainAccount) {
        try {
            commit(types.CONNECTION_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.DomainAccount.CONNECTION,
                    params: testDomainAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CONNECTION_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.DomainAccount.CONNECTED);
                dispatch('toastMessage', `${testDomainAccount.account_name} ${message}`, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CONNECTION_FAILURE, params);
        }
    },

    /**
     * パスワードランダム化対象のノードドメイン管理者アカウントと登録済みアカウントの取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id ドメインID
     * @param {any} word 検索キーワード
     * @param {any} account_type_display アカウント種別
     * @param {any} randomize_type ランダマイズ方式
     * @param {any} connection_type 認証方式
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async randomizableAccounts(
        { dispatch, commit },
        {
            id,
            word = '',
            account_type_display = '',
            randomize_type = undefined,
            connection_type = undefined,
            sort,
            pageNumber,
            perPage,
        } = {}
    ) {
        const payload = {
            word: word,
            account_type_display: account_type_display,
            randomize_type: randomize_type,
            connection_type: connection_type,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
            page_number: pageNumber,
            per_page: perPage,
        };
        try {
            commit(types.NODE_DOMAIN_RANDOMIZABLE_ACCOUNT_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.NodeDomain.RANDOMIZABLE_ACCOUNTS.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.NODE_DOMAIN_RANDOMIZABLE_ACCOUNT_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.NODE_DOMAIN_RANDOMIZABLE_ACCOUNT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 検索済みデータをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearWork({ commit }) {
        commit(types.CLEAR_WORK);
    },

    /**
     * エラーをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async resetError({ commit }) {
        commit(types.RESET_ERROR);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
